import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { json, type ActionFunctionArgs } from '@remix-run/node'
import { Form, useActionData } from '@remix-run/react'
import { format, fromUnixTime } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { z } from 'zod'
import { Field } from '../components/field.tsx'
import { Button } from '../ui/button.tsx'
import { Hint } from '../ui/hint.tsx'
import { Label } from '../ui/label.tsx'
import { Select } from '../ui/select.tsx'
import { Textarea } from '../ui/textarea.tsx'

const MAX_UNIXTIME_SECOND = 16980778160

const dataMergeTypeType = {
  SUM: 'Somme',
  AVERAGE: 'Moyenne',
  MEDIAN: 'Médiane',
}

const BucketSizeOptionsToCustom: Record<string, string> = {
  ONE_MINUTE: '1m',
  ONE_HOUR: '1h',
  ONE_DAY: '1d',
  ONE_WEEK: '1w',
  CUSTOM: '',
}

const BucketSizeOptions = {
  ONE_MINUTE: '1 minute',
  ONE_HOUR: '1 hour',
  ONE_DAY: '1 day',
  ONE_WEEK: '1 week',
  CUSTOM: 'Other...',
}

const separatorRegex = /[ \t,;:]/

const schema = z
  .object({
    userData: z.string(),
    dataMergeType: z.enum([
      Object.keys(dataMergeTypeType)[0],
      ...Object.keys(dataMergeTypeType).slice(1),
    ]),
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    customBucketSize: z
      .string()
      .regex(/^[0-9]{1,3}[mhdwsy]$/i)
      .optional(),
    bucketSize: z.enum([
      Object.keys(BucketSizeOptions)[0],
      ...Object.keys(BucketSizeOptions).slice(1),
    ]),
  })
  .refine(({ bucketSize, customBucketSize }) => !(bucketSize === 'CUSTOM' && !customBucketSize), {
    path: ['customBucketSize'],
    message: 'Custom bucket size invalid',
  })

function parseTs(date: string) {
  const ts = Number(date)
  if (isNaN(ts) || date.trim() === '') return null

  if (ts > MAX_UNIXTIME_SECOND) return fromUnixTime(ts / 1000)

  return fromUnixTime(ts)
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const submission = parse(formData, { schema })

  if (!submission.value) {
    return json({ data: null, submission })
  }

  // Parse data
  const { userData, dataMergeType, customBucketSize, bucketSize, startDate, endDate } =
    submission.value
  let parseData = userData
    .split('\n')
    .map((line) => {
      const splitted = line.split(separatorRegex)
      const timestamp = parseTs(splitted[0])
      const value = Number(splitted[1]?.trim()) || 1

      if (!timestamp) return null
      return { timestamp, value }
    })
    .filter(Boolean) as { timestamp: Date; value: number }[]

  if (startDate) {
    parseData = parseData.filter((e) => e.timestamp.getTime() >= startDate.getTime())
  }
  if (endDate) {
    parseData = parseData.filter((e) => e.timestamp.getTime() <= endDate.getTime())
  }

  const bucketSizeCustom: string =
    (bucketSize === 'CUSTOM' ? customBucketSize : BucketSizeOptionsToCustom[bucketSize]) || '1d'

  let bucketFunction = (d: Date) => '1'
  const timeLabel = bucketSizeCustom[bucketSizeCustom?.length - 1]
  if (timeLabel == 'h') bucketFunction = (d) => format(d, 'd/M/y h')
  else if (timeLabel == 'd') bucketFunction = (d) => format(d, 'd/M/y')
  else if (timeLabel == 'w') bucketFunction = (d) => format(d, 'y I')
  else if (timeLabel == 'm') bucketFunction = (d) => format(d, 'd/M/y HH:mm')
  else if (timeLabel == 'M') bucketFunction = (d) => format(d, 'M/y')
  else if (timeLabel == 's') bucketFunction = (d) => format(d, 'd/M/y HH:mm:ss')
  else if (timeLabel == 'y') bucketFunction = (d) => format(d, 'y')

  const aggData = parseData.reduce(
    (prev, cur) => {
      const bucket = bucketFunction(cur.timestamp)
      if (prev[bucket]) prev[bucket].push(cur.value)
      else prev[bucket] = [cur.value]

      return prev
    },
    {} as Record<string, number[]>,
  )

  let mergeValues = (values: number[]) => values.reduce((a, b) => a + b, 0)
  if (dataMergeType === 'AVERAGE')
    mergeValues = (values: number[]) => {
      const sum = values.reduce((a, b) => a + b, 0)
      return sum / values.length
    }
  else if (dataMergeType === 'MEDIAN') {
    mergeValues = (values: number[]) => {
      values = [...values].sort((a, b) => a - b)
      const half = Math.floor(values.length / 2)
      return values.length % 2 ? values[half] : (values[half - 1] + values[half]) / 2
    }
  }

  const data = Object.entries(aggData).map(([key, value]) => {
    return { label: key, value: mergeValues(value) }
  })

  return json({ submission, data })
}

export default function Index() {
  const { submission: lastSubmission, data } = useActionData<typeof action>() || {}

  const [form, fields] = useForm({
    constraint: getFieldsetConstraint(schema),
    lastSubmission,
    defaultValue: {
      from: format(new Date(), 'dd/mm/y h:i:s'),
      end: format(new Date(), 'dd/mm/y h:i:s'),
    },
  })

  const bucketSizeRef = useRef<HTMLSelectElement>(null)
  const [showCustom, setShowCustom] = useState(false)
  useEffect(() => {
    if (bucketSizeRef.current) {
      setShowCustom(bucketSizeRef.current?.value === 'CUSTOM')
    }
  }, [bucketSizeRef])

  const tooltipStyle = {
    color: 'blue',
  }

  return (
    <Form method="post" {...form.props} preventScrollReset className="flex flex-col gap-4">
      {/* <Link to={routerPaths['/graph/:id']({ id: 25 })}>Ceci est un lien</Link> */}
      <div>
        <Label>Data</Label>
        <Textarea
          error={fields.userData.error}
          {...conform.input(fields.userData)}
          cols={50}
          rows={6}
        ></Textarea>
        <Hint error={fields.userData.error}></Hint>
      </div>
      <div>
        <Label>Merge type</Label>
        <Select error={fields.dataMergeType.error} {...conform.input(fields.dataMergeType)}>
          {Object.keys(dataMergeTypeType).map((type) => (
            <option key={type} value={type}>
              {dataMergeTypeType[type as keyof typeof dataMergeTypeType]}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label>Bucket size</Label>
        <Select
          {...conform.input(fields.bucketSize)}
          ref={bucketSizeRef}
          onChange={(event) => {
            setShowCustom(event.currentTarget.value === 'CUSTOM')
          }}
        >
          {Object.keys(BucketSizeOptions).map((type) => (
            <option key={type} value={type}>
              {BucketSizeOptions[type as keyof typeof BucketSizeOptions]}
            </option>
          ))}
        </Select>
      </div>

      <Field field={fields.startDate} label="From" />
      <Field field={fields.endDate} label="To" />

      {showCustom && <Field field={fields.customBucketSize} />}
      <Button type="submit" primary>
        Process
      </Button>

      {data && (
        <BarChart width={1024} height={300} data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" />
          <YAxis />

          <Tooltip labelStyle={tooltipStyle} />
          <Bar dataKey="value" fill="#8884d8" />
        </BarChart>
      )}
    </Form>
  )
}
